/**
 * Calculator Context
 *
 * Purpose: Global state management for the FSS calculator
 * Usage: Provides calculator state and actions to all components
 * Features: State persistence, calculation results, user selections
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { calculateRequirements } from '../utils/calculationEngine';
import validationRules from '../utils/validationRules';
import { parseShareableUrl } from '../utils/urlGenerator';

// Initial state structure
const initialState = {
  selectedState: null,
  selectedBusinessType: null,
  calculationResults: null,
  currentStep: 1,
  isLoading: false,
  errors: [],
  warnings: [],
  history: []
};

// Action types
const actionTypes = {
  SET_STATE: 'SET_STATE',
  SET_BUSINESS_TYPE: 'SET_BUSINESS_TYPE',
  SET_RESULTS: 'SET_RESULTS',
  SET_STEP: 'SET_STEP',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_WARNINGS: 'SET_WARNINGS',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  RESET_CALCULATOR: 'RESET_CALCULATOR',
  GO_BACK: 'GO_BACK',
  ADD_TO_HISTORY: 'ADD_TO_HISTORY'
};

// Reducer function
const calculatorReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_STATE:
      return {
        ...state,
        selectedState: action.payload,
        currentStep: 2,
        errors: []
      };

    case actionTypes.SET_BUSINESS_TYPE:
      return {
        ...state,
        selectedBusinessType: action.payload,
        errors: []
      };

    case actionTypes.SET_RESULTS:
      return {
        ...state,
        calculationResults: action.payload,
        currentStep: 3,
        isLoading: false,
        errors: []
      };

    case actionTypes.SET_STEP:
      return {
        ...state,
        currentStep: action.payload
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        errors: Array.isArray(action.payload) ? action.payload : [action.payload],
        isLoading: false
      };

    case actionTypes.SET_WARNINGS:
      return {
        ...state,
        warnings: action.payload
      };

    case actionTypes.CLEAR_ERRORS:
      return {
        ...state,
        errors: [],
        warnings: []
      };

    case actionTypes.RESET_CALCULATOR:
      return {
        ...initialState
      };

    case actionTypes.GO_BACK:
      const previousStep = Math.max(1, state.currentStep - 1);
      return {
        ...state,
        currentStep: previousStep,
        errors: [],
        warnings: [],
        // Clear results if going back from results page
        calculationResults: previousStep < 3 ? null : state.calculationResults
      };

    case actionTypes.ADD_TO_HISTORY:
      return {
        ...state,
        history: [...state.history, action.payload]
      };

    default:
      return state;
  }
};

// Create context
const CalculatorContext = createContext();

// Provider component
export const CalculatorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  // Check for URL parameters on mount
  useEffect(() => {
    const urlParams = parseShareableUrl();
    if (urlParams.state && urlParams.businessTypeId) {
      // Auto-populate from URL
      actions.setState(urlParams.state);
      actions.setBusinessType(urlParams.businessTypeId);
      actions.calculate(urlParams.state, urlParams.businessTypeId);
    } 
     // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);

  // Action creators
  const actions = {
    /**
     * Set selected state
     */
    setState: (stateCode) => {
      // Validate state
      const validation = validationRules.validateState(stateCode);
      
      if (!validation.isValid) {
        dispatch({ type: actionTypes.SET_ERROR, payload: validation.errors });
        return false;
      }

      if (validation.warnings.length > 0) {
        dispatch({ type: actionTypes.SET_WARNINGS, payload: validation.warnings });
      }

      dispatch({ type: actionTypes.SET_STATE, payload: stateCode });
      dispatch({ type: actionTypes.ADD_TO_HISTORY, payload: { step: 1, state: stateCode } });
      return true;
    },

    /**
     * Set selected business type
     */
    setBusinessType: (businessTypeId) => {
      // Validate business type
      const validation = validationRules.validateIndustry(businessTypeId, state.selectedState);
      
      if (!validation.isValid) {
        dispatch({ type: actionTypes.SET_ERROR, payload: validation.errors });
        return false;
      }

      if (validation.warnings.length > 0) {
        dispatch({ type: actionTypes.SET_WARNINGS, payload: validation.warnings });
      }

      dispatch({ type: actionTypes.SET_BUSINESS_TYPE, payload: businessTypeId });
      dispatch({ type: actionTypes.ADD_TO_HISTORY, payload: { step: 2, businessType: businessTypeId } });
      return true;
    },

    /**
     * Calculate requirements
     */
    calculate: async (stateCode, businessTypeId) => {
      // Use provided values or state values
      const state = stateCode || state.selectedState;
      const businessType = businessTypeId || state.selectedBusinessType;

      if (!state || !businessType) {
        dispatch({ 
          type: actionTypes.SET_ERROR, 
          payload: 'Please select both state and business type' 
        });
        return false;
      }

      // Validate complete selection
      const validation = validationRules.validateSelection(state, businessType);
      
      if (!validation.isValid) {
        dispatch({ type: actionTypes.SET_ERROR, payload: validation.errors });
        return false;
      }

      try {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });

        // Simulate async calculation (remove this in production if not needed)
        await new Promise(resolve => setTimeout(resolve, 500));

        // Calculate requirements
        const results = calculateRequirements(state, businessType);
        
        dispatch({ type: actionTypes.SET_RESULTS, payload: results });
        dispatch({ type: actionTypes.ADD_TO_HISTORY, payload: { step: 3, results } });
        
        return true;
      } catch (error) {
        dispatch({ 
          type: actionTypes.SET_ERROR, 
          payload: error.message || 'An error occurred during calculation' 
        });
        return false;
      }
    },

    /**
     * Set current step manually
     */
    setStep: (step) => {
      dispatch({ type: actionTypes.SET_STEP, payload: step });
    },

    /**
     * Go back to previous step
     */
    goBack: () => {
      dispatch({ type: actionTypes.GO_BACK });
    },

    /**
     * Clear all errors
     */
    clearErrors: () => {
      dispatch({ type: actionTypes.CLEAR_ERRORS });
    },

    /**
     * Reset calculator to initial state
     */
    reset: () => {
      dispatch({ type: actionTypes.RESET_CALCULATOR });
    },

    /**
     * Set error manually
     */
    setError: (error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error });
    }
  };

  // Context value
  const value = {
    state,
    actions,
    // Convenience getters
    isStateSelected: !!state.selectedState,
    isBusinessTypeSelected: !!state.selectedBusinessType,
    hasResults: !!state.calculationResults,
    canGoBack: state.currentStep > 1,
    canCalculate: !!(state.selectedState && state.selectedBusinessType)
  };

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
};

// Custom hook to use calculator context
export const useCalculatorContext = () => {
  const context = useContext(CalculatorContext);
  if (!context) {
    throw new Error('useCalculatorContext must be used within a CalculatorProvider');
  }
  return context;
};

// Export action types for testing
export { actionTypes };

export default CalculatorContext;
